import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { CrewPicker } from "@/components/CrewPicker";
import { api, errMsg, BOAT_CATEGORIES, slug } from "@/lib/api";

const crewFromBoat = (b) =>
  b && {
    ...(b.crew_user_id ? { user_id: b.crew_user_id, tipo: "utente" } : { pending_id: b.crew_pending_id, tipo: "in_attesa" }),
    nome: b.crew_nome,
    cognome: b.crew_cognome,
    data_nascita: b.crew_data_nascita,
  };

export const BoatForm = ({ open, onOpenChange, boat, onSaved }) => {
  const [categoria, setCategoria] = useState("");
  const [velico, setVelico] = useState("");
  const [crew, setCrew] = useState(null);
  const [ruolo, setRuolo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCategoria(boat?.categoria || "");
      setVelico(boat?.numero_velico || "");
      setCrew(crewFromBoat(boat) || null);
      setRuolo(boat?.ruolo || "");
    }
  }, [open, boat]);

  const submit = async (e) => {
    e.preventDefault();
    if (!categoria) return toast.error("Seleziona la categoria della barca");
    if (!ruolo) return toast.error("Indica il tuo ruolo a bordo");
    if (!crew) return toast.error("Indica il secondo membro dell'equipaggio");
    setSaving(true);
    const payload = { categoria, numero_velico: velico, ruolo, crew: { user_id: crew.user_id, pending_id: crew.pending_id, nome: crew.nome, cognome: crew.cognome, data_nascita: crew.data_nascita } };
    try {
      if (boat) await api.put(`/boats/${boat.id}`, payload);
      else await api.post("/boats", payload);
      toast.success(boat ? "Barca aggiornata" : "Barca registrata");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="boat-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase">{boat ? "Modifica barca" : "Nuova barca"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Categoria barca</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger data-testid="boat-category-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {BOAT_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`boat-category-option-${slug(c)}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Numero velico</Label>
              <Input placeholder="ITA 123" value={velico} onChange={(e) => setVelico(e.target.value)} required data-testid="boat-sail-number-input" />
              <p className="text-[11px] text-muted-foreground mt-1">3 lettere seguite da numeri</p>
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Il tuo ruolo a bordo</Label>
            <RadioGroup value={ruolo} onValueChange={setRuolo} className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="timoniere" data-testid="boat-role-timoniere" />Timoniere</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="prodiere" data-testid="boat-role-prodiere" />Prodiere</label>
            </RadioGroup>
          </div>
          <div>
            <Label className="mb-2 block">Secondo membro dell'equipaggio{ruolo && ` (${ruolo === "timoniere" ? "prodiere" : "timoniere"})`}</Label>
            <CrewPicker value={crew} onChange={setCrew} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="boat-form-cancel-button">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="boat-form-submit-button">
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
